import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getMetrics, type Metric, type GetMetricsParams } from "../api/client";
import type { GraphAggregation, ServiceSlug } from "../api/client";
import type { TimeRange } from "../utils/time";

export interface UseMetricsOptions {
  serviceSlug: ServiceSlug | null;
  timeRange: TimeRange;
  serviceNames?: string[];
  serviceInstances?: string[];
  metricNames?: string[];
  enabled?: boolean;
  /** When changed, invalidates cache and refetches (e.g. from refresh controls). */
  refreshToken?: number;
}

export interface UseMetricsResult {
  metrics: Metric[];
  /** Aggregation info from the server (type "none" = raw; otherwise e.g. avg + interval). */
  aggregation: GraphAggregation;
  loading: boolean;
  isFetching: boolean;
  error: string | null;
}

/**
 * Hook for fetching metrics with filters.
 *
 * @param options - Configuration options for fetching metrics
 * @returns Object containing metrics, loading state, and error
 */
export function useMetrics({
  serviceSlug,
  timeRange,
  serviceNames = [],
  serviceInstances = [],
  metricNames = [],
  enabled = true,
  refreshToken,
}: UseMetricsOptions): UseMetricsResult {
  const serviceNamesKey = serviceNames.join(",");
  const serviceInstancesKey = serviceInstances.join(",");
  const metricNamesKey = metricNames.join(",");

  const query = useQuery({
    queryKey: [
      "metrics",
      serviceSlug,
      timeRange.from.toISOString(),
      timeRange.to.toISOString(),
      serviceNamesKey,
      serviceInstancesKey,
      metricNamesKey,
      refreshToken,
    ],
    enabled: !!serviceSlug && enabled && serviceNames.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const allMetrics: Metric[] = [];
      let aggregation: GraphAggregation = { type: "none" };

      for (const serviceName of serviceNames) {
        const params: GetMetricsParams = {
          serviceSlug: serviceSlug!,
          serviceName,
          startTime: timeRange.from,
          endTime: timeRange.to,
          signal,
        };

        const { data: serviceMetrics, aggregation: agg } = await getMetrics(params);
        allMetrics.push(...serviceMetrics);
        if (agg.type !== "none") aggregation = agg;
      }

      let filtered = allMetrics;

      if (serviceInstances.length > 0) {
        filtered = filtered.filter(
          (m) => m.service_instance && serviceInstances.includes(m.service_instance),
        );
      }

      if (metricNames.length > 0) {
        filtered = filtered.filter((m) => metricNames.includes(m.metric_name));
      }

      return { metrics: filtered, aggregation };
    },
  });

  if (!serviceSlug || !enabled || serviceNames.length === 0) {
    return { metrics: [], aggregation: { type: "none" }, loading: false, isFetching: false, error: null };
  }

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load metrics"
        : null;
  const payload = query.data ?? { metrics: [], aggregation: { type: "none" } };
  return {
    metrics: payload.metrics ?? [],
    aggregation: payload.aggregation ?? { type: "none" },
    loading: query.isLoading,
    isFetching: query.isFetching,
    error,
  };
}
